import { useCallback, useMemo, useState } from "react";
import { Platform, Pressable, ScrollView, View } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import "../../theme";
import { useCombobox } from "../../hooks/use-combobox";
import { Text } from "../Text";
import { SearchInputField } from "../SearchInput/parts/SearchInputField";
import { SearchLoading } from "../SearchInput/parts/SearchLoading";
import { SearchEmpty } from "../SearchInput/parts/SearchEmpty";
import { Chip } from "../Chip";
import { MultiSelectOption } from "./MultiSelectOption";
import type { GetItemProps, MultiOptionState, MultiSelectInputSlotProps, MultiSelectProps } from "./types";

// ---------------------------------------------------------------------------
// Default filter: case-insensitive substring match on label
// ---------------------------------------------------------------------------

function defaultFilter<T>(
  options: readonly T[],
  inputValue: string,
  getOptionLabel: (option: T) => string,
): readonly T[] {
  if (!inputValue) return options;
  const lower = inputValue.toLowerCase();
  return options.filter((o) => getOptionLabel(o).toLowerCase().includes(lower));
}

// ---------------------------------------------------------------------------
// MultiSelect component
// ---------------------------------------------------------------------------

function MultiSelectInner<T>(props: MultiSelectProps<T>) {
  const {
    options,
    value: selectedValues,
    onChange,
    getOptionLabel,
    getOptionId,
    inputValue: controlledInputValue,
    onInputChange,
    filterOptions,
    filterSelectedOptions = false,
    maxTags = 3,
    chipLayout = "inline",
    isLoading = false,
    placeholder = "Select…",
    label,
    textInputProps,
    groupBy,
    renderOption,
    renderGroup,
    renderValue,
    renderInput,
    renderLoading,
    renderEmpty,
  } = props;

  // --- Input value (controlled or uncontrolled) ---
  const [internalInputValue, setInternalInputValue] = useState("");
  const inputIsControlled = controlledInputValue !== undefined;
  const inputValue = inputIsControlled ? controlledInputValue : internalInputValue;

  const handleInputChange = useCallback(
    (text: string) => {
      if (!inputIsControlled) {
        setInternalInputValue(text);
      }
      onInputChange?.(text);
    },
    [inputIsControlled, onInputChange],
  );

  // --- Selected IDs set for O(1) lookup ---
  const selectedIdSet = useMemo(() => new Set(selectedValues.map(getOptionId)), [selectedValues, getOptionId]);

  // --- Filtered options ---
  const filteredOptions = useMemo(() => {
    let opts = filterOptions ? filterOptions(options, inputValue) : defaultFilter(options, inputValue, getOptionLabel);
    if (filterSelectedOptions) {
      opts = opts.filter((o) => !selectedIdSet.has(getOptionId(o)));
    }
    return opts;
  }, [options, inputValue, filterOptions, getOptionLabel, filterSelectedOptions, selectedIdSet, getOptionId]);

  // --- Toggle selection ---
  const handleToggle = useCallback(
    (option: T) => {
      const id = getOptionId(option);
      if (selectedIdSet.has(id)) {
        onChange(
          selectedValues.filter((v) => getOptionId(v) !== id),
          "removeOption",
        );
      } else {
        onChange([...selectedValues, option], "selectOption");
      }
    },
    [getOptionId, selectedIdSet, onChange, selectedValues],
  );

  // --- Remove a specific selected value ---
  const handleRemove = useCallback(
    (option: T) => {
      const id = getOptionId(option);
      onChange(
        selectedValues.filter((v) => getOptionId(v) !== id),
        "removeOption",
      );
    },
    [getOptionId, onChange, selectedValues],
  );

  // --- Combobox hook (disableCloseOnSelect for multi) ---
  const { isOpen, close, highlightedIndex, setHighlightedIndex, containerRef, handleKeyPress, handleInputFocus } =
    useCombobox<T>({
      options: filteredOptions,
      getOptionId,
      onSelect: handleToggle,
      disableCloseOnSelect: true,
    });

  // --- Show/hide dropdown ---
  const showDropdown = isOpen && (isLoading || filteredOptions.length > 0 || inputValue.length > 0);

  // --- Grouped options (preserves flat index for keyboard nav) ---
  const groupedOptions = useMemo(() => {
    if (!groupBy) return null;
    const groups: { label: string; items: { option: T; flatIndex: number }[] }[] = [];
    let currentGroup: { label: string; items: { option: T; flatIndex: number }[] } | undefined;
    filteredOptions.forEach((option, flatIndex) => {
      const gl = groupBy(option);
      if (!currentGroup || gl !== currentGroup.label) {
        currentGroup = { label: gl, items: [] };
        groups.push(currentGroup);
      }
      currentGroup.items.push({ option, flatIndex });
    });
    return groups;
  }, [groupBy, filteredOptions]);

  // --- Item props getter for renderValue ---
  const getItemProps: GetItemProps = useCallback(
    (_option: unknown, index: number) => ({
      key: getOptionId(selectedValues[index] as T),
      onDelete: () => handleRemove(selectedValues[index] as T),
    }),
    [getOptionId, selectedValues, handleRemove],
  );

  // --- Render ---
  const chipsStyle = [styles.chips, chipLayout === "wrap" ? styles.chipsWrap : styles.chipsInline];

  const chipsContent =
    selectedValues.length > 0 ?
      renderValue ? <View style={chipsStyle}>{renderValue(selectedValues, getItemProps)}</View>
      : <View style={chipsStyle}>
          {selectedValues.length > maxTags ?
            <Chip label={`${selectedValues.length.toString()} selected`} onDelete={() => onChange([], "clear")} />
          : selectedValues.map((item) => (
              <Chip key={getOptionId(item)} label={getOptionLabel(item)} onDelete={() => handleRemove(item)} />
            ))
          }
        </View>
    : undefined;

  const inputSlotProps: MultiSelectInputSlotProps = {
    value: inputValue,
    onChangeText: handleInputChange,
    onFocus: handleInputFocus,
    onKeyPress: handleKeyPress,
    placeholder: selectedValues.length === 0 ? placeholder : undefined,
    label,
    textInputProps,
    leftContent: chipsContent,
  };

  return (
    <View ref={containerRef} style={[styles.container, showDropdown && styles.containerOpen]}>
      {/* Input with chips inline */}
      {renderInput ? renderInput(inputSlotProps) : <SearchInputField {...inputSlotProps} />}

      {/* Dropdown */}
      {showDropdown && (
        <>
          {Platform.OS !== "web" && <Pressable style={styles.backdrop} onPress={close} />}
          <View style={styles.dropdown}>
            {isLoading ?
              renderLoading ?
                renderLoading()
              : <SearchLoading />
            : filteredOptions.length === 0 ?
              renderEmpty ?
                renderEmpty()
              : <SearchEmpty />
            : <ScrollView style={styles.list} keyboardShouldPersistTaps="handled">
                {groupedOptions ?
                  groupedOptions.map((group, groupIndex) => (
                    <View key={`${group.label}-${groupIndex.toString()}`}>
                      {renderGroup ?
                        renderGroup(group.label)
                      : <View style={styles.groupHeader}>
                          <Text textStyle="xs/semibold" color="textMuted">
                            {group.label}
                          </Text>
                        </View>
                      }
                      {group.items.map(({ option, flatIndex }) => {
                        const optionState: MultiOptionState = {
                          focused: flatIndex === highlightedIndex,
                          selected: selectedIdSet.has(getOptionId(option)),
                          index: flatIndex,
                        };

                        if (renderOption) {
                          return (
                            <Pressable
                              key={getOptionId(option)}
                              onPress={() => handleToggle(option)}
                              onHoverIn={() => setHighlightedIndex(flatIndex)}
                            >
                              {renderOption(option, optionState)}
                            </Pressable>
                          );
                        }

                        return (
                          <MultiSelectOption
                            key={getOptionId(option)}
                            label={getOptionLabel(option)}
                            state={optionState}
                            onPress={() => handleToggle(option)}
                            onHoverIn={() => setHighlightedIndex(flatIndex)}
                          />
                        );
                      })}
                    </View>
                  ))
                : filteredOptions.map((option, index) => {
                    const optionState: MultiOptionState = {
                      focused: index === highlightedIndex,
                      selected: selectedIdSet.has(getOptionId(option)),
                      index,
                    };

                    if (renderOption) {
                      return (
                        <Pressable
                          key={getOptionId(option)}
                          onPress={() => handleToggle(option)}
                          onHoverIn={() => setHighlightedIndex(index)}
                        >
                          {renderOption(option, optionState)}
                        </Pressable>
                      );
                    }

                    return (
                      <MultiSelectOption
                        key={getOptionId(option)}
                        label={getOptionLabel(option)}
                        state={optionState}
                        onPress={() => handleToggle(option)}
                        onHoverIn={() => setHighlightedIndex(index)}
                      />
                    );
                  })
                }
              </ScrollView>
            }
          </View>
        </>
      )}
    </View>
  );
}

MultiSelectInner.displayName = "MultiSelect";

const MultiSelect = MultiSelectInner as <T>(props: MultiSelectProps<T>) => ReturnType<typeof MultiSelectInner>;

export { MultiSelect };
export type { MultiSelectProps, MultiOptionState, MultiSelectInputSlotProps, GetItemProps } from "./types";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create((theme) => ({
  container: {
    position: "relative",
    overflow: "visible",
  },
  containerOpen: {
    zIndex: theme.zIndex.inputOpen,
    overflow: "visible",
  },
  chips: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.xxs,
    paddingLeft: theme.spacing.md,
  },
  chipsInline: {
    maxWidth: "50%",
    overflow: "hidden",
  },
  chipsWrap: {
    flexWrap: "wrap",
    paddingVertical: theme.spacing.xs,
    // Sized by its chips up to this cap, wrapping beyond it. The remaining width
    // is left to the search input, which keeps its own `flex: 1`.
    maxWidth: "80%",
  },
  backdrop: {
    position: "absolute",
    top: -1000,
    left: -1000,
    right: -1000,
    bottom: -1000,
    zIndex: theme.zIndex.backdrop,
  },
  dropdown: {
    position: "absolute",
    // Anchored to the field's actual height rather than a fixed offset, so a
    // wrapping chip row that grows the input does not get overlapped. Matches
    // the previous fixed `top: 48` for a default medium (44px) field.
    top: "100%",
    marginTop: 4,
    left: 0,
    right: 0,
    maxHeight: 300,
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.border,
    borderRadius: theme.spacing.xl,
    ...theme.shadows.sm,
    zIndex: theme.zIndex.dropdown,
    overflow: "hidden",
  },
  list: {
    maxHeight: 300,
  },
  groupHeader: {
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 4,
  },
}));
